import { useContext, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import authService from "../../../services/authService";

const useAuth = () => {
  const context  = useContext(AuthContext);
  const navigate = useNavigate();
  // ── فاز ۸: خوندن ?next= از URL صفحه‌ی لاگین/ثبت‌نام فعلی ──────────
  // مثلاً وقتی از HomePage روی یه دوره کلیک می‌کنی، می‌ری
  // /register?next=/courses/123 — بعد از ثبت‌نام باید دقیقاً همونجا برگردی.
  const [searchParams] = useSearchParams();

  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");

  const { state, dispatch } = context;

  const login = useCallback(async (phone, password) => {
    dispatch({ type: "AUTH_LOADING" });
    try {
      const data = await authService.login(phone, password);

      // backend { access, refresh, user } برمیگردونه
      let user = data.user;
      if (!user) {
        user = await authService.getMe();
      }

      localStorage.setItem("cached_user", JSON.stringify(user));
      dispatch({ type: "AUTH_SUCCESS", payload: { user, token: data.access } });

      const routes = {
        admin:   "/admin/dashboard",
        teacher: "/teacher/dashboard",
        support: "/support/dashboard",
        staff:   "/admin/dashboard",
        student: "/dashboard",
      };

      // اگه next تو URL بود (و مسیر داخلی معتبر بود) اولویت با اونه،
      // وگرنه همون رفتار قبلی: مستقیم داشبورد نقش کاربر
      const nextPath = searchParams.get("next");
      const target = (nextPath && nextPath.startsWith("/"))
        ? nextPath
        : (routes[user?.role] ?? "/dashboard");

      navigate(target, { replace: true });
      return user;

    } catch (err) {
      dispatch({ type: "AUTH_IDLE" });

      // خطای واضح بر اساس status code
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      let msg = "خطا در ورود";
      if (status === 401) msg = "شماره تلفن یا رمز عبور اشتباه است";
      else if (status === 404) msg = "سرور پیدا نشد — backend رو چک کن";
      else if (detail) msg = detail;

      throw new Error(msg);
    }
  }, [dispatch, navigate, searchParams]);

  const register = useCallback(async (payload) => {
    dispatch({ type: "AUTH_LOADING" });
    try {
      const data = await authService.register(payload);
      let user = data.user;
      if (!user) user = await authService.getMe();
      localStorage.setItem("cached_user", JSON.stringify(user));
      dispatch({ type: "AUTH_SUCCESS", payload: { user, token: data.access } });

      // فاز ۸: همون next اینجا هم اعمال می‌شه
      const nextPath = searchParams.get("next");
      const target = (nextPath && nextPath.startsWith("/")) ? nextPath : "/dashboard";

      navigate(target, { replace: true });
      return data;
    } catch (err) {
      dispatch({ type: "AUTH_IDLE" });
      throw err;
    }
  }, [dispatch, navigate, searchParams]);

  const logout = useCallback(() => {
    authService.logout();
    localStorage.removeItem("cached_user");
    dispatch({ type: "AUTH_LOGOUT" });
    navigate("/login", { replace: true });
  }, [dispatch, navigate]);

  const refreshUser = useCallback(async () => {
    try {
      const user = await authService.getMe();
      localStorage.setItem("cached_user", JSON.stringify(user));
      dispatch({ type: "AUTH_UPDATE_USER", payload: user });
      return user;
    } catch { /* silent */ }
  }, [dispatch]);

  const verifyEmail    = useCallback(async () => ({ success: true }), []);
  const forgotPassword = useCallback(async () => ({ success: true }), []);

  return {
    currentUser:     state.user,
    token:           state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading:       state.isLoading,
    error:           state.error,
    login, logout, register, verifyEmail, forgotPassword, refreshUser,
  };
};

export default useAuth;